/**
 * One gap in edit mode's «Слова» tab (handoff §15.3): its form, its form label with
 * the correction picker, the read-only dictionary reference and the accepted answers.
 *
 * Everything the card changes goes to the edit draft; nothing reaches the server until
 * the one save. A gap that exists only in this draft (a form just marked) has no
 * server row yet, so its label cannot be corrected and no answer can be added until the
 * text is saved; its card says so.
 */
import { useState } from 'react'
import { X } from 'lucide-react'
import { useLocale } from '../../../i18n/context'
import { useGapLabelOptions } from '../../../hooks/useTexts'
import { useTextEditStore } from '../../../stores/textEditStore'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../../utils/controlSurface'
import type { EditDraft } from '../../../utils/textEdit'
import type { GapLabelEdit, TextGap } from '../../../types'
import { OverflowMenu } from '../TextList'
import { Eyebrow, GhostButton, SecondaryPill } from '../ui'

interface Props {
  textId: string
  /** The gap's stored form. */
  form: string
  /** The saved gap; null for one marked in this draft. */
  gap: TextGap | null
  places: number
  draft: EditDraft
}

/** What the gap shows as its form label once the draft's edit is applied. */
type Shown = { kind: 'label'; raw: string } | { kind: 'hidden' } | { kind: 'none' }

function shownLabel(gap: TextGap | null, edit: GapLabelEdit | undefined): { shown: Shown; corrected: boolean } {
  const classifier: Shown = gap?.label ? { kind: 'label', raw: gap.label } : { kind: 'none' }
  if (edit?.label !== undefined) return { shown: { kind: 'label', raw: edit.label }, corrected: true }
  if (edit?.hide) return { shown: { kind: 'hidden' }, corrected: true }
  if (edit?.clear) return { shown: classifier, corrected: false }
  if (gap?.label_corrected) {
    return { shown: gap.corrected_label ? { kind: 'label', raw: gap.corrected_label } : { kind: 'hidden' }, corrected: true }
  }
  return { shown: classifier, corrected: false }
}

const norm = (s: string) => s.trim().toLowerCase()

export default function GapCard({ textId, form, gap, places, draft }: Props) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const store = useTextEditStore()
  const [picking, setPicking] = useState(false)
  const edit = draft.labelEdits[form]
  const { shown, corrected } = shownLabel(gap, edit)

  const answers = draft.alternativeEdits[form] ?? { add: [], remove: [] }
  const saved = (gap?.alternatives ?? []).filter((a) => !answers.remove.includes(a.id))

  const labelText = (raw: string) => tr.learn.formLabel(raw)
  const hasLabelRow = gap !== null

  return (
    <section
      data-testid="edit-gap-card"
      className={CONTROL_SURFACE}
      onContextMenu={suppressTouchContextMenu}
      style={{
        borderRadius: 16, background: 'var(--bg-surface)', padding: '10px 4px 12px 14px', fontFamily: 'var(--font-ui)',
        border: `1px solid ${picking ? 'var(--accent)' : 'var(--border)'}`,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36 }}>
        <span style={{
          fontFamily: 'var(--font-content)', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)',
          minWidth: 0, overflowWrap: 'anywhere',
        }}>
          {form}
        </span>
        {places > 1 && <span style={{ fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0 }}>{te.inPlaces(places)}</span>}
        <span style={{ flex: 1 }} />
        <OverflowMenu
          ariaLabel={te.gapMenu(form)}
          items={[{
            key: 'remove', label: te.removeGap, icon: <X size={15} aria-hidden />,
            onSelect: () => store.removeForm(form),
          }]}
        />
      </div>

      {!hasLabelRow && (
        <p data-testid="edit-gap-new-note" style={{ margin: 0, paddingRight: 10, fontSize: 12, color: 'var(--text-tertiary)' }}>
          {te.newGapNote}
        </p>
      )}

      {hasLabelRow && !picking && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36, paddingRight: 4 }}>
          {shown.kind === 'label' ? (
            <>
              <Eyebrow>{te.formEyebrow}</Eyebrow>
              <span data-testid="edit-gap-label" style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-primary)' }}>
                {labelText(shown.raw)}
              </span>
            </>
          ) : (
            <span data-testid="edit-gap-label" style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
              {shown.kind === 'hidden' ? te.formHidden : te.formUnset}
            </span>
          )}
          <GhostButton onClick={() => setPicking(true)} data-testid="edit-gap-change" style={{ height: 36, fontSize: 13, flexShrink: 0 }}>
            {shown.kind === 'label' || shown.kind === 'hidden' ? te.change : te.specify}
          </GhostButton>
        </div>
      )}

      {hasLabelRow && picking && gap && (
        <LabelPicker
          textId={textId}
          gap={gap}
          shown={shown}
          corrected={corrected}
          onDone={() => setPicking(false)}
          onApply={(next) => { store.setLabelEdit(form, next); setPicking(false) }}
        />
      )}

      {gap?.reference && (
        <p data-testid="edit-gap-reference" style={{ margin: 0, paddingRight: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
          {te.dictionary(gap.reference.base_form, gap.reference.translation)}
        </p>
      )}

      {hasLabelRow && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 10 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
            <Eyebrow>{te.acceptedEyebrow}</Eyebrow>
            {saved.length + answers.add.length === 0 && (
              <span data-testid="edit-gap-no-answers" style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{te.noAccepted}</span>
            )}
            {saved.map((a) => (
              <AnswerChip key={a.id} answer={a.answer} onRemove={() => store.removeAnswer(form, { id: a.id })} />
            ))}
            {answers.add.map((a) => (
              <AnswerChip key={`new-${a}`} answer={a} onRemove={() => store.removeAnswer(form, { added: a })} />
            ))}
          </div>
          <AddAnswer
            form={form}
            existing={[...saved.map((a) => a.answer), ...answers.add]}
            onAdd={(answer) => store.addAnswer(form, answer)}
          />
        </div>
      )}
    </section>
  )
}

function AnswerChip({ answer, onRemove }: { answer: string; onRemove: () => void }) {
  const { tr } = useLocale()
  return (
    <span
      data-testid="edit-gap-answer"
      style={{
        display: 'inline-flex', alignItems: 'center', height: 36, paddingLeft: 12, borderRadius: 'var(--radius-pill)',
        border: '1px solid var(--border)', background: 'var(--bg-surface)',
        fontFamily: 'var(--font-content)', fontSize: 15, color: 'var(--text-primary)',
      }}
    >
      {answer}
      <button
        type="button"
        onClick={onRemove}
        aria-label={tr.textEdit.removeAnswer(answer)}
        style={{
          width: 36, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)',
        }}
      >
        <X size={14} aria-hidden />
      </button>
    </span>
  )
}

function AddAnswer({ form, existing, onAdd }: { form: string; existing: string[]; onAdd: (answer: string) => void }) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const [value, setValue] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  const submit = () => {
    const answer = value.trim()
    if (!answer) return
    // The server compares under the text fold and is the judge; these catch the plain cases
    // here so the whole save is not refused for them.
    if (norm(answer) === norm(form)) return setProblem(te.answerIsForm)
    if (existing.some((a) => norm(a) === norm(answer))) return setProblem(te.answerExists)
    onAdd(answer)
    setValue('')
    setProblem(null)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="text"
          value={value}
          onChange={(e) => { setValue(e.target.value); setProblem(null) }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
          aria-label={te.addAnswerLabel(form)}
          placeholder={te.addAnswerPlaceholder}
          data-testid="edit-gap-answer-input"
          autoCapitalize="off"
          style={{
            flex: 1, minWidth: 0, height: 40, boxSizing: 'border-box', padding: '0 12px', borderRadius: 8,
            border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
            fontFamily: 'var(--font-content)', fontSize: 15,
          }}
        />
        <SecondaryPill height={40} onClick={submit} disabled={!value.trim()} data-testid="edit-gap-answer-add" style={{ flexShrink: 0 }}>
          {te.addAnswer}
        </SecondaryPill>
      </div>
      {problem && <p role="alert" style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)' }}>{problem}</p>}
    </div>
  )
}

type Choice = { kind: 'form'; raw: string } | { kind: 'hide' } | { kind: 'custom' } | null

/** «Форма в тексте»: the forms the classifier knows for the part of speech, hiding, or an own label. */
function LabelPicker({ textId, gap, shown, corrected, onDone, onApply }: {
  textId: string
  gap: TextGap
  shown: Shown
  corrected: boolean
  onDone: () => void
  onApply: (edit: GapLabelEdit | null) => void
}) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const options = useGapLabelOptions(textId, gap.id, true)
  const forms = options.data?.forms ?? []
  const max = options.data?.max_custom_label_chars ?? 40

  const initialCustom = shown.kind === 'label' && options.data && !forms.includes(shown.raw) ? shown.raw : ''
  const [choice, setChoice] = useState<Choice | undefined>(undefined)
  const [custom, setCustom] = useState<string | undefined>(undefined)

  // Until the learner touches the picker, it shows the current state.
  const effectiveCustom = custom ?? initialCustom
  const effectiveChoice: Choice =
    choice !== undefined ? choice
    : effectiveCustom ? { kind: 'custom' }
    : shown.kind === 'label' ? { kind: 'form', raw: shown.raw }
    : shown.kind === 'hidden' ? { kind: 'hide' }
    : null

  const customValue = effectiveCustom.trim()
  const canSave = effectiveChoice !== null && (effectiveChoice.kind !== 'custom' || customValue.length > 0)

  const save = () => {
    if (!effectiveChoice) return
    if (effectiveChoice.kind === 'hide') return onApply({ hide: true })
    if (effectiveChoice.kind === 'custom') return onApply({ label: effectiveCustom })
    // The classifier's own form is no correction.
    if (effectiveChoice.raw === gap.label) return onApply(gap.label_corrected ? { clear: true } : null)
    onApply({ label: effectiveChoice.raw })
  }

  const row = (selected: boolean) => ({
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40, padding: '6px 10px', borderRadius: 8,
    border: 'none', cursor: 'pointer', textAlign: 'left' as const, fontFamily: 'var(--font-ui)', fontSize: 14,
    color: 'var(--text-primary)', background: selected ? 'var(--accent-soft)' : 'transparent',
  })
  const radio = (selected: boolean) => (
    <span
      aria-hidden
      style={{
        width: 18, height: 18, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
        border: `2px solid ${selected ? 'var(--accent)' : 'var(--border-strong, var(--border))'}`,
        background: selected ? 'radial-gradient(var(--accent) 0 40%, transparent 45%)' : 'transparent',
      }}
    />
  )

  return (
    <div data-testid="edit-gap-picker" style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 10 }}>
      <Eyebrow>{te.formInText}</Eyebrow>
      {options.isLoading && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{te.loadingOptions}</p>}
      {options.isError && <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{te.optionsFailed}</p>}
      {options.data && (
        <div role="radiogroup" aria-label={te.formInText} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {forms.map((raw) => {
            const selected = effectiveChoice?.kind === 'form' && effectiveChoice.raw === raw
            return (
              <button
                key={raw}
                type="button"
                role="radio"
                aria-checked={selected}
                data-testid="edit-gap-option"
                onClick={() => { setChoice({ kind: 'form', raw }); setCustom('') }}
                style={row(selected)}
              >
                {radio(selected)}
                <span style={{ flex: 1, minWidth: 0 }}>{tr.learn.formLabel(raw)}</span>
              </button>
            )
          })}
          <button
            type="button"
            role="radio"
            aria-checked={effectiveChoice?.kind === 'hide'}
            data-testid="edit-gap-option-hide"
            onClick={() => { setChoice({ kind: 'hide' }); setCustom('') }}
            style={row(effectiveChoice?.kind === 'hide')}
          >
            {radio(effectiveChoice?.kind === 'hide')}
            <span style={{ flex: 1, minWidth: 0 }}>{te.hideForm}</span>
          </button>
        </div>
      )}
      {options.data && (
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={effectiveCustom}
            maxLength={max}
            onChange={(e) => { setCustom(e.target.value); setChoice(e.target.value ? { kind: 'custom' } : null) }}
            placeholder={te.customPlaceholder}
            aria-label={te.customPlaceholder}
            data-testid="edit-gap-custom"
            style={{
              width: '100%', height: 44, boxSizing: 'border-box', padding: '0 64px 0 12px', borderRadius: 8,
              border: `1px solid ${effectiveChoice?.kind === 'custom' ? 'var(--accent)' : 'var(--border)'}`,
              background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', fontSize: 14,
            }}
          />
          <span
            data-testid="edit-gap-custom-count"
            style={{ position: 'absolute', right: 12, top: 15, fontSize: 12, color: 'var(--text-tertiary)' }}
          >
            {te.customCount(effectiveCustom.length, max)}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
        {corrected && (
          <GhostButton
            onClick={() => onApply(gap.label_corrected ? { clear: true } : null)}
            data-testid="edit-gap-reset"
            style={{ marginRight: 'auto', fontSize: 13 }}
          >
            {te.resetForm}
          </GhostButton>
        )}
        <GhostButton onClick={onDone} data-testid="edit-gap-cancel" style={{ fontSize: 13 }}>{te.cancel}</GhostButton>
        <SecondaryPill height={40} onClick={save} disabled={!canSave} data-testid="edit-gap-save-form">{te.saveForm}</SecondaryPill>
      </div>
    </div>
  )
}
